/**
 * Phase 7++++ — StationsEditor
 *
 * Editable list of buffet *stations* (matches the backend `BuffetStation`
 * model). Replaces the old single-textarea pattern for Hot Kitchen /
 * Cold Kitchen / Pastry. Each station holds:
 *
 *   {
 *     name:        str,                  // station name (free text)
 *     description: str,                  // optional one-liner
 *     dishes:      List[Dish],           // per-dish material picker
 *     station_type:"custom"|"live"|"standard",
 *     station_id:  Optional[str],        // ref to lib_{live,standard}_stations
 *   }
 *
 * Picking a "Live" or "Standard" station from the dropdown SNAPSHOTS the
 * lib doc's name / description / dishes (with materials!) into the
 * station card — the meal-block keeps its own editable copy, so
 * deleting the lib doc later doesn't wipe the day's plan.
 *
 * Accent prop tunes the colored chrome (orange = hot, cyan = cold,
 * yellow = pastry) so callers don't have to know how to style it.
 */
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Plus, X, ChevronDown, ChevronRight } from "lucide-react";
import DishListEditor from "@/components/mealplan/DishListEditor";

// Tailwind class bundles, keyed by `accent`. Default to slate.
const ACCENTS = {
  orange: {
    label: "Hot Kitchen",
    labelClass: "text-orange-800",
    cardClass: "border-orange-200 bg-orange-50/40",
    chipBase: "border-orange-300 bg-orange-50 text-orange-800",
  },
  cyan: {
    label: "Cold Kitchen",
    labelClass: "text-cyan-800",
    cardClass: "border-cyan-200 bg-cyan-50/40",
    chipBase: "border-cyan-300 bg-cyan-50 text-cyan-800",
  },
  yellow: {
    label: "Pastry",
    labelClass: "text-yellow-900",
    cardClass: "border-yellow-300 bg-yellow-100/60",
    chipBase: "border-yellow-300 bg-yellow-100 text-yellow-900",
  },
  slate: {
    label: "Stations",
    labelClass: "text-slate-700",
    cardClass: "border-slate-200 bg-slate-50/40",
    chipBase: "border-slate-300 bg-slate-50 text-slate-700",
  },
};

const TYPE_CHIPS = {
  live: { label: "Live", className: "border-red-300 bg-red-50 text-red-800" },
  standard: { label: "Standard", className: "border-slate-300 bg-slate-50 text-slate-700" },
  custom: { label: "Custom", className: "border-emerald-300 bg-emerald-50 text-emerald-800" },
  // Phase 16 (F5/CO1) — Pizza station chip.
  pizza: { label: "Pizza", className: "border-amber-400 bg-amber-50 text-amber-900" },
  // Phase 16 (F13) — Carving station chip. Distinct rose tint so it
  // reads as a sibling of Pizza but not the same colour.
  carving: { label: "Carving", className: "border-rose-400 bg-rose-50 text-rose-900" },
};

function StationCard({
  value,
  onChange,
  onRemove,
  liveStationsLib,
  standardStationsLib,
  materialsLib,
  // Phase 14 — list of {id,name,order} ship-buffet-lines available for
  // tagging. Empty/missing → only the "Unassigned" option renders.
  linesLib,
  // Phase 16 (F5/CO1) — Pizza catalog + saved bundles. Used by the
  // station-type picker to offer a "Pizza Station" mode that pre-fills
  // dishes from a chosen `lib_pizza_sets` bundle (snapshot only).
  pizzasLib,
  pizzaSetsLib,
  // Phase 16 (F13) — Carving catalog + saved bundles. Symmetric mirror
  // of Pizza; selecting a carving set bulk-fills the dish list.
  carvingsLib,
  carvingSetsLib,
  // B6 — controls whether the LINE / ISLAND dropdown row is rendered.
  // The voyage-day editor (where the ship + its lines are known) keeps
  // the default `true`. The Library Theme Buffet edit dialog passes
  // `false` so the dropdown disappears entirely instead of dead-ending
  // on a permanent "Unassigned" placeholder. Line assignment is a
  // voyage-day decision, not a library decision (Phase 14 locked).
  showLinePicker = true,
  tidPrefix,
  accent,
}) {
  const v = value || {
    name: "",
    description: "",
    dishes: [],
    station_type: "custom",
    station_id: null,
    line_id: null,
    line_name: null,
  };
  const stationType = v.station_type || "custom";

  // Phase 15 (F4) — per-card collapsed state. Persisted to localStorage
  // keyed by the stable client `_key` or `station_id`. Falls back to
  // the in-memory default when neither is set (new ad-hoc station that
  // hasn't been saved yet).
  // CRITICAL: the body is rendered with Tailwind `hidden` (display:none)
  // not conditional return — this way the user's in-progress dish
  // inputs and react state are NOT unmounted when the card collapses.
  const persistId = v._key || v.station_id || null;
  const lsKey = persistId ? `vm:station_collapsed:${persistId}` : null;
  const [open, setOpen] = useState(() => {
    if (!lsKey) return true;
    try {
      return window.localStorage.getItem(lsKey) !== "1";
    } catch (_e) {
      return true;
    }
  });
  const toggleOpen = () => {
    setOpen((prev) => {
      const next = !prev;
      if (lsKey) {
        try {
          if (next) {
            window.localStorage.removeItem(lsKey);
          } else {
            window.localStorage.setItem(lsKey, "1");
          }
        } catch (_e) {
          // localStorage may be disabled (private mode / quota) — collapse
          // still works in-memory, just won't survive a reload.
        }
      }
      return next;
    });
  };

  const update = (patch) => onChange({ ...v, ...patch });

  const applyLinePick = (val) => {
    if (val === "__unassigned__") {
      update({ line_id: null, line_name: null });
      return;
    }
    const ln = (linesLib || []).find((x) => x.id === val);
    if (!ln) {
      update({ line_id: null, line_name: null });
      return;
    }
    update({ line_id: ln.id, line_name: ln.name });
  };
  const lineSelectValue = v.line_id || "__unassigned__";

  const pickerValue =
    stationType === "pizza" ? "pizza:__none__" :
    stationType === "carving" ? "carving:__none__" :
    stationType === "live" && v.station_id ? `live:${v.station_id}` :
    stationType === "standard" && v.station_id ? `standard:${v.station_id}` :
    "custom";

  const applyPick = (selectVal) => {
    if (selectVal === "custom") {
      update({
        station_type: "custom", station_id: null,
        pizza_set_id: null, pizza_set_name: null,
        carving_set_id: null, carving_set_name: null,
      });
      return;
    }
    // Phase 16 (F5/CO1) — "Pizza" sentinel: set station_type to pizza
    // but leave the actual pizza-set / dish list to be filled in by
    // the Pizza Set dropdown below. Pre-fills station name to "Pizza
    // Station" only when the user hadn't typed one yet.
    if (selectVal === "pizza:__none__") {
      update({
        station_type: "pizza",
        station_id: null,
        pizza_set_id: null,
        pizza_set_name: null,
        carving_set_id: null,
        carving_set_name: null,
        name: (v.name || "").trim() || "Pizza Station",
        description: (v.description || "").trim() ||
          "Wood-fired / live pizza station",
      });
      return;
    }
    // Phase 16 (F13) — "Carving" sentinel. Same shape as the pizza
    // sentinel; clears every set ref + pre-fills name to "Carving
    // Station" only when the user hadn't typed one yet.
    if (selectVal === "carving:__none__") {
      update({
        station_type: "carving",
        station_id: null,
        pizza_set_id: null,
        pizza_set_name: null,
        carving_set_id: null,
        carving_set_name: null,
        name: (v.name || "").trim() || "Carving Station",
        description: (v.description || "").trim() ||
          "Hot-carved roasts · jus on the side",
      });
      return;
    }
    const [kind, id] = selectVal.split(":");
    const lib =
      kind === "live"
        ? (liveStationsLib || []).find((s) => s.id === id)
        : (standardStationsLib || []).find((s) => s.id === id);
    if (!lib) {
      update({ station_type: "custom", station_id: null });
      return;
    }
    // SNAPSHOT — only fill empty fields so the user's typed edits survive
    // a re-pick (same pattern Live Station picker uses).
    update({
      station_type: kind,
      station_id: lib.id,
      pizza_set_id: null,
      pizza_set_name: null,
      carving_set_id: null,
      carving_set_name: null,
      name: (v.name || "").trim() || lib.name || "",
      description: (v.description || "").trim() || lib.description || "",
      dishes:
        (v.dishes || []).length > 0
          ? v.dishes
          : (lib.dishes || []).map((d) =>
              typeof d === "string"
                ? { name: d, material_id: null, material_name: "" }
                : {
                    name: d.name || "",
                    material_id: d.material_id || null,
                    material_name: d.material_name || "",
                  },
            ),
    });
  };

  // Phase 16 (F5/CO1) — Pizza Set picker handler. Looks up the chosen
  // set + resolves pizza_ids to pizza docs from `pizzasLib`. Snapshot
  // copy: each resolved pizza becomes a dish row with the pizza name
  // and description (description rendered as the "Notes" line on PDF).
  // Source bundle reference is denormalized (`pizza_set_id` +
  // `pizza_set_name`) so deleting the bundle never breaks the day.
  const applyPizzaSetPick = (setId) => {
    if (setId === "__none__") {
      update({ pizza_set_id: null, pizza_set_name: null });
      return;
    }
    const set = (pizzaSetsLib || []).find((s) => s.id === setId);
    if (!set) return;
    const pizzaById = new Map(
      (pizzasLib || []).map((p) => [p.id, p])
    );
    const resolvedDishes = (set.pizza_ids || [])
      .map((pid) => pizzaById.get(pid))
      .filter(Boolean)
      .map((p) => ({
        _key: (typeof crypto !== "undefined" && crypto.randomUUID)
          ? crypto.randomUUID()
          : `pz_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        // Phase 16 — pizza dish rows render as "Name — description" on
        // the PDF via the standard Dish renderer; we don't yet have a
        // dedicated material slot for pizzas so material_id stays null.
        name: p.description
          ? `${p.name} — ${p.description}`
          : p.name,
        material_id: null,
        material_name: "",
      }));
    update({
      pizza_set_id: set.id,
      pizza_set_name: set.name,
      // Replace dishes wholesale only when the current list is empty.
      // Otherwise append — preserves any custom pizzas the user added.
      dishes:
        (v.dishes || []).length > 0
          ? [...v.dishes, ...resolvedDishes]
          : resolvedDishes,
    });
  };

  // Phase 16 (F13) — Carving Set picker handler. Symmetric mirror of
  // `applyPizzaSetPick`. Looks up the chosen set in `carvingSetsLib`,
  // resolves `carving_ids` against `carvingsLib`, snapshots each into
  // a dish row formatted as "Name — description". `carving_set_id` +
  // `carving_set_name` are denormalized on the BuffetStation so a
  // later library delete doesn't break the day's render.
  const applyCarvingSetPick = (setId) => {
    if (setId === "__none__") {
      update({ carving_set_id: null, carving_set_name: null });
      return;
    }
    const set = (carvingSetsLib || []).find((s) => s.id === setId);
    if (!set) return;
    const carvingById = new Map(
      (carvingsLib || []).map((c) => [c.id, c])
    );
    const resolvedDishes = (set.carving_ids || [])
      .map((cid) => carvingById.get(cid))
      .filter(Boolean)
      .map((c) => ({
        _key: (typeof crypto !== "undefined" && crypto.randomUUID)
          ? crypto.randomUUID()
          : `cv_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        name: c.description
          ? `${c.name} — ${c.description}`
          : c.name,
        material_id: null,
        material_name: "",
      }));
    update({
      carving_set_id: set.id,
      carving_set_name: set.name,
      dishes:
        (v.dishes || []).length > 0
          ? [...v.dishes, ...resolvedDishes]
          : resolvedDishes,
    });
  };

  const chip = TYPE_CHIPS[stationType] || TYPE_CHIPS.custom;
  const dishCount = (v.dishes || []).filter((d) => (d?.name || "").trim()).length;

  return (
    <div
      className={cn("rounded border p-2", ACCENTS[accent]?.cardClass || ACCENTS.slate.cardClass)}
      data-testid={tidPrefix}
    >
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={toggleOpen}
          className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-white/60"
          aria-label={open ? "Collapse station" : "Expand station"}
          data-testid={`${tidPrefix}-toggle`}
        >
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
        <Select value={pickerValue} onValueChange={applyPick}>
          <SelectTrigger
            className="h-7 flex-1 text-[10px]"
            data-testid={`${tidPrefix}-station-select`}
          >
            <SelectValue placeholder="Pick station type…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="custom">— Custom (no library) —</SelectItem>
            {/* Phase 16 (F5/CO1) — Pizza Station sentinel. Always
                offered, even when pizza libs are empty (the dropdown
                inside the card lets the user pick a set later). */}
            <div className="px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-amber-800">
              Pizza
            </div>
            <SelectItem value="pizza:__none__">Pizza Station</SelectItem>
            {/* Phase 16 (F13) — Carving Station sentinel. Mirror of Pizza. */}
            <div className="px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-rose-800">
              Carving
            </div>
            <SelectItem value="carving:__none__">Carving Station</SelectItem>
            {(liveStationsLib || []).length > 0 && (
              <div className="px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-red-700">
                Live Stations
              </div>
            )}
            {(liveStationsLib || []).map((s) => (
              <SelectItem key={`live:${s.id}`} value={`live:${s.id}`}>
                {s.name}
              </SelectItem>
            ))}
            {(standardStationsLib || []).length > 0 && (
              <div className="px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-slate-700">
                Standard Stations
              </div>
            )}
            {(standardStationsLib || []).map((s) => (
              <SelectItem key={`standard:${s.id}`} value={`standard:${s.id}`}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span
          className={cn(
            "shrink-0 rounded border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide",
            chip.className,
          )}
          data-testid={`${tidPrefix}-type-chip`}
        >
          {chip.label}
        </span>
        {!open && (
          <span className="shrink-0 text-[10px] italic text-muted-foreground">
            {(v.name || "").trim() || <em>(no name)</em>} · {dishCount} dish{dishCount === 1 ? "" : "es"}
          </span>
        )}
        <button
          type="button"
          onClick={onRemove}
          className="shrink-0 rounded p-1 text-red-600 hover:bg-red-50"
          aria-label="Remove station"
          data-testid={`${tidPrefix}-remove`}
        >
          <X className="h-3 w-3" />
        </button>
      </div>
      {/* Phase 15 (F4) — body uses Tailwind `hidden` (display: none) NOT
          conditional render so the user's in-progress dish edits +
          local component state survive a collapse/expand cycle. */}
      <div className={cn("mt-1.5 space-y-1.5", !open && "hidden")}>
          {/* Phase 16 (F5/CO1) — Pizza Set picker (only when station_type
              is "pizza"). Selecting a set bulk-loads pizza dishes from
              the bundle's pizza_ids; user can then edit / remove rows. */}
          {stationType === "pizza" && (
            <div className="flex items-center gap-1.5 rounded border border-amber-300 bg-amber-50/60 px-1.5 py-1">
              <Label
                className="shrink-0 text-[9px] font-semibold uppercase tracking-wider text-amber-900"
              >
                Pizza Set
              </Label>
              <Select
                value={v.pizza_set_id || "__none__"}
                onValueChange={applyPizzaSetPick}
              >
                <SelectTrigger
                  className="h-6 flex-1 text-[10px]"
                  data-testid={`${tidPrefix}-pizza-set-select`}
                >
                  <SelectValue placeholder="— Load pizzas from set —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— No bundle (custom) —</SelectItem>
                  {(pizzaSetsLib || []).map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                      {Array.isArray(s.pizza_ids) && (
                        <span className="ml-1 text-[9px] text-muted-foreground">
                          · {s.pizza_ids.length} pizza{s.pizza_ids.length === 1 ? "" : "s"}
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {v.pizza_set_name && (
                <span className="shrink-0 text-[9px] italic text-amber-800">
                  current: {v.pizza_set_name}
                </span>
              )}
            </div>
          )}
          {/* Phase 16 (F13) — Carving Set picker (only when station_type
              is "carving"). Snapshots `lib_carving_sets` → carving dishes. */}
          {stationType === "carving" && (
            <div className="flex items-center gap-1.5 rounded border border-rose-300 bg-rose-50/60 px-1.5 py-1">
              <Label
                className="shrink-0 text-[9px] font-semibold uppercase tracking-wider text-rose-900"
              >
                Carving Set
              </Label>
              <Select
                value={v.carving_set_id || "__none__"}
                onValueChange={applyCarvingSetPick}
              >
                <SelectTrigger
                  className="h-6 flex-1 text-[10px]"
                  data-testid={`${tidPrefix}-carving-set-select`}
                >
                  <SelectValue placeholder="— Load carvings from set —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— No bundle (custom) —</SelectItem>
                  {(carvingSetsLib || []).map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                      {Array.isArray(s.carving_ids) && (
                        <span className="ml-1 text-[9px] text-muted-foreground">
                          · {s.carving_ids.length} item{s.carving_ids.length === 1 ? "" : "s"}
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {v.carving_set_name && (
                <span className="shrink-0 text-[9px] italic text-rose-800">
                  current: {v.carving_set_name}
                </span>
              )}
            </div>
          )}
          {/* Phase 14 — Line / Island dropdown. Always rendered so the
              user can move a station into / out of Unassigned. When the
              voyage's ship has no lines configured, the dropdown only
              shows the Unassigned option.

              B6 — gated by `showLinePicker`. The Library Theme Buffet
              edit dialog passes `false` here because the ship + its
              lines aren't known in a library context; line assignment
              is a voyage-day decision (Phase 14 locked design). */}
          {showLinePicker && (
            <div className="flex items-center gap-1.5">
              <Label
                className="shrink-0 text-[9px] font-semibold uppercase tracking-wider text-slate-600"
              >
                Line / Island
              </Label>
              <Select value={lineSelectValue} onValueChange={applyLinePick}>
                <SelectTrigger
                  className="h-7 flex-1 text-[10px]"
                  data-testid={`${tidPrefix}-line-select`}
                >
                  <SelectValue placeholder="— Unassigned —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__unassigned__">— Unassigned —</SelectItem>
                  {(linesLib || []).map((ln) => (
                    <SelectItem key={ln.id} value={ln.id}>
                      {ln.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Input
            value={v.name || ""}
            onChange={(e) => update({ name: e.target.value })}
            placeholder="Station name (e.g. Pasta Live, Carving, Salad Bar)"
            className="h-7 text-xs"
            data-testid={`${tidPrefix}-name`}
          />
          <Input
            value={v.description || ""}
            onChange={(e) => update({ description: e.target.value })}
            placeholder="Description (optional, shows above dishes on PDF)"
            className="h-7 text-[11px] italic"
            data-testid={`${tidPrefix}-description`}
          />
          <DishListEditor
            value={v.dishes || []}
            onChange={(next) => update({ dishes: next })}
            materialsLib={materialsLib}
            tidPrefix={`${tidPrefix}-dishes`}
            compact
          />
        </div>
    </div>
  );
}

export default function StationsEditor({
  value,
  onChange,
  liveStationsLib,
  standardStationsLib,
  materialsLib,
  // Phase 14 — pass-through to StationCard's line dropdown.
  linesLib,
  // Phase 16 (F5/CO1) — Pizza catalog + saved bundles.
  pizzasLib,
  pizzaSetsLib,
  // Phase 16 (F13) — Carving catalog + saved bundles.
  carvingsLib,
  carvingSetsLib,
  // B6 — pass-through to StationCard. Default true (voyage-day editor).
  // Library Theme Buffet edit dialog passes `false`.
  showLinePicker = true,
  tidPrefix = "stations",
  accent = "slate",
  label,
}) {
  const stations = Array.isArray(value) ? value : [];
  const accentCfg = ACCENTS[accent] || ACCENTS.slate;

  const updateAt = (idx, next) =>
    onChange(stations.map((s, i) => (i === idx ? next : s)));
  const removeAt = (idx) => onChange(stations.filter((_, i) => i !== idx));
  const add = () =>
    onChange([
      ...stations,
      {
        // Phase 8 code-quality — stable client-side key for the React
        // reconciler, stripped server-side by Pydantic `extra="ignore"`.
        _key: (typeof crypto !== "undefined" && crypto.randomUUID)
          ? crypto.randomUUID()
          : `st_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
        name: "",
        description: "",
        dishes: [],
        station_type: "custom",
        station_id: null,
        // Phase 14 — new stations always start "Unassigned".
        line_id: null,
        line_name: null,
      },
    ]);

  return (
    <div className="space-y-1.5" data-testid={tidPrefix}>
      <div className="flex items-center justify-between">
        <span
          className={cn(
            "text-[10px] font-semibold uppercase tracking-wide",
            accentCfg.labelClass,
          )}
        >
          {label || accentCfg.label}
        </span>
        <span className="text-[9px] text-muted-foreground">
          {stations.length} station{stations.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="space-y-1.5">
        {stations.length === 0 && (
          <p className="text-[10px] italic text-muted-foreground">
            No stations yet — click “+ Add station” below.
          </p>
        )}
        {stations.map((s, idx) => (
          <StationCard
            // Stable composite: prefer client-stamped `_key`, then
            // library `station_id` (when picked from a lib), then
            // legacy index. Index is the LAST resort for rows that
            // pre-date this fix in saved meal plans.
            key={s._key || s.station_id || `legacy-station-${idx}`}
            value={s}
            onChange={(next) => updateAt(idx, next)}
            onRemove={() => removeAt(idx)}
            liveStationsLib={liveStationsLib}
            standardStationsLib={standardStationsLib}
            materialsLib={materialsLib}
            linesLib={linesLib}
            pizzasLib={pizzasLib}
            pizzaSetsLib={pizzaSetsLib}
            carvingsLib={carvingsLib}
            carvingSetsLib={carvingSetsLib}
            showLinePicker={showLinePicker}
            tidPrefix={`${tidPrefix}-${idx}`}
            accent={accent}
          />
        ))}
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={add}
        className="h-6 px-2 text-[10px]"
        data-testid={`${tidPrefix}-add`}
      >
        <Plus className="h-3 w-3" />
        Add station
      </Button>
    </div>
  );
}
